import { ProjectState } from '../types/project';
import { Chapter } from '../types';
import { getChapterContent } from './projectStateUtils';

export interface SelectedSections {
  frontMatter: boolean;
  chapters: boolean; 
  backMatter: boolean;
}

/**
 * Converts project state to markdown format for both preview and export
 * This ensures content parity between what users see in preview and what gets exported
 */
export function projectStateToMarkdown(
  state: ProjectState,
  selected: SelectedSections
): string {
  let markdown = `# ${state.blueprint?.worldBuildingSuggestions?.selectedStoryTitle || 'My Work'}\n\n`;
  if (state.authorDetails.authorName) markdown += `## By ${state.authorDetails.authorName}\n\n`;

  if (selected.frontMatter) {
    const fm = state.frontMatter;
    if (fm.titlePageContent) markdown += `${fm.titlePageContent}\n\n---\n\n`;
    if (fm.copyrightPageContent) markdown += `### Copyright\n\n${fm.copyrightPageContent}\n\n---\n\n`;
    if (fm.dedication) markdown += `### Dedication\n\n${fm.dedication}\n\n---\n\n`;
    if (fm.epigraph) markdown += `### Epigraph\n\n${fm.epigraph}\n\n---\n\n`;
    if (fm.tableOfContents && fm.tableOfContents !== '<!-- Table of Contents will be generated here -->') {
      markdown += `### Table of Contents\n\n${fm.tableOfContents.replace(/<\/?[^>]+>/g, '').trim()}\n\n---\n\n`;
    }
    if (fm.foreword) markdown += `### Foreword\n\n${fm.foreword}\n\n---\n\n`;
    if (fm.preface) markdown += `### Preface\n\n${fm.preface}\n\n---\n\n`;
    if (fm.acknowledgmentsFront) markdown += `### Acknowledgments\n\n${fm.acknowledgmentsFront}\n\n---\n\n`;
    if (fm.prologue) markdown += `### Prologue\n\n${fm.prologue}\n\n---\n\n`;
    if (fm.introduction) markdown += `### Introduction\n\n${fm.introduction}\n\n---\n\n`;
  }

  if (selected.chapters) {
    const mode = state.exportChapterContentPreference === 'preview' ? 'preview' : 'content';
    state.chapters.forEach((ch, i) => {
      markdown += `### Chapter ${i + 1}: ${ch.title}\n\n`;
      markdown += `${getChapterContent(ch as Chapter, mode)}\n\n`;
    });
  }

  if (selected.backMatter) {
    const bm = state.backMatter;
    if (bm.epilogue) markdown += `### Epilogue\n\n${bm.epilogue}\n\n---\n\n`;
    if (bm.afterword) markdown += `### Afterword\n\n${bm.afterword}\n\n---\n\n`;
    if (bm.authorsNote) markdown += `### Author's Note\n\n${bm.authorsNote}\n\n---\n\n`;
    if (bm.acknowledgmentsBack) markdown += `### Acknowledgments\n\n${bm.acknowledgmentsBack}\n\n---\n\n`;
    if (bm.appendices?.length) {
      markdown += `### Appendices\n\n`;
      bm.appendices.forEach(app => markdown += `#### ${app.title}\n\n${app.content}\n\n`);
      markdown += `---\n\n`;
    }
    if (bm.glossary?.length) {
      markdown += `### Glossary\n\n`;
      bm.glossary.forEach(g => markdown += `**${g.term}**: ${g.definition}\n\n`);
      markdown += `---\n\n`;
    }
    if (bm.bibliography?.length) {
      markdown += `### Bibliography\n\n`;
      bm.bibliography.forEach(b => markdown += `${b.citation}\n\n`);
      markdown += `---\n\n`;
    }
    if (bm.index) markdown += `### Index\n\n${bm.index}\n\n---\n\n`;
    if (bm.readingGroupGuide) markdown += `### Reading Group Guide\n\n${bm.readingGroupGuide}\n\n---\n\n`;
    if (bm.teaserNextBook) markdown += `### Teaser for Next Book\n\n${bm.teaserNextBook}\n\n---\n\n`;
    const about = bm.aboutTheAuthor || state.authorDetails?.authorBio;
    if (about) markdown += `### About the Author\n\n${about}\n\n`;
  }

  return markdown;
}